import {
  ChevronRightIcon,
  InformationCircleIcon,
} from "@heroicons/react/24/outline";
import clsx from "clsx";
import {
  ComponentPropsWithoutRef,
  FC,
  isValidElement,
  ReactElement,
  ReactNode,
  useId,
} from "react";
import { ReactSVG } from "react-svg";
import { twMerge } from "tailwind-merge";
import Button, { BUTTON_STYLE } from "./Button";
import CustomTooltip from "./CustomTooltip";
import Typography, { TypeScale } from "./Typography";

export type PropertyTileProps = {
  title?: string | ReactElement;
  titleVariant?: TypeScale;
  subtitleVariant?: TypeScale;
  resetTooltip?: string;
  resetEnabled?: boolean;
  subtitle?: string | ReactElement;
  // `info` accepts either a plain string (renders via the global tooltip
  // through data-tooltip-content) or a ReactNode for rich content (renders
  // through a per-instance CustomTooltip with JSX children). Both produce
  // the same hover-revealed `i` icon next to the title.
  info?: string | ReactNode;
  /** Rich info only: classes for its tooltip (a wider one for a visual). */
  infoTooltipClassName?: string;
  onReset?: () => void;
  onClickAccordion?: (open: boolean) => void;
  leading?: ReactElement<HTMLDivElement>;
  trailing?: ReactElement<HTMLDivElement>;
  headingMargin?: boolean;
  fitButtonLayout?: boolean;
  sticky?: boolean;
  variant?: "primary" | "secondary";
  size?: "s" | "m" | "l";
  symmetricPadding?: boolean;
  trailingPadding?: boolean;
  titleAlignment?: "start" | "center";
  overflowVisible?: boolean;
  // Lets title content (e.g. a gutter-bleeding button) render outside the
  // title wrappers instead of being clipped by their `truncate`. Disables
  // title text truncation for this tile.
  titleOverflowVisible?: boolean;
  resetButtonClassName?: string;
  trailingWrapperClassName?: string;
} & Omit<ComponentPropsWithoutRef<"div">, "title">;

export const PROPERTY_TILE_VARIANT_STYLES = {
  s: {
    padding: "p-5 pr-4 py-1",
    symmetricPadding: "!px-4",
  },
  m: {
    padding: "p-5 pr-4 py-3",
    symmetricPadding: "!px-4",
  },
  l: {
    padding: "p-5 pr-4 py-4",
    symmetricPadding: "!px-4",
  },
};

const TITLE_VARIANT_STYLES = {
  primary: "text-foreground",
  secondary: "text-foreground-muted dark:text-foreground-secondary",
};

const extractFirstText = (node: ReactNode): string => {
  if (!node) return "";
  if (typeof node === "string") return node;
  if (typeof node === "number") return String(node);

  if (Array.isArray(node)) {
    for (const child of node) {
      const text = extractFirstText(child);
      if (text) return text;
    }
  }

  if (isValidElement(node)) {
    const element = node as ReactElement<{ children?: ReactNode }>;
    return extractFirstText(element.props.children);
  }

  return "";
};

/** Shared title/subtitle stack, including rows with their own native button. */
export function PropertyTileText({
  title,
  subtitle,
  subtitleVariant = "overline",
  subtitleClassName,
  component: Component = "div",
  className,
}: {
  title?: ReactNode;
  subtitle?: ReactNode;
  subtitleVariant?: TypeScale;
  subtitleClassName?: string;
  component?: "div" | "span";
  className?: string;
}) {
  return (
    <Component className={twMerge("flex flex-col", className)}>
      {title}
      {subtitle && (
        <Typography variant={subtitleVariant}
          className={twMerge("text-foreground-muted whitespace-pre-wrap", subtitleClassName)}>
          {subtitle}
        </Typography>
      )}
    </Component>
  );
}

const PropertyTile: FC<PropertyTileProps> = ({
  title,
  titleVariant = "subtitle3",
  resetTooltip,
  resetEnabled = true,
  subtitle,
  subtitleVariant = "overline",
  info,
  infoTooltipClassName,
  leading,
  trailing,
  onReset,
  onClickAccordion,
  fitButtonLayout,
  sticky = false,
  variant = "primary",
  size = "m",
  symmetricPadding = false,
  trailingPadding = false,
  titleAlignment = "start",
  overflowVisible = false,
  titleOverflowVisible = false,
  resetButtonClassName,
  trailingWrapperClassName,
  ...rest
}) => {
  const isClickable = !!rest.onClick;

  // String info uses the shared global tooltip (data-tooltip-content). Rich
  // (ReactNode) info renders through a per-instance CustomTooltip whose
  // children are real React, so it can include inline icons or other JSX.
  const isStringInfo = typeof info === "string";
  const richInfoTooltipId = useId();
  const tooltipAnchorId = info
    ? isStringInfo
      ? "global"
      : richInfoTooltipId
    : undefined;
  const tooltipAnchorContent = isStringInfo ? (info as string) : undefined;
  const INFO_TOOLTIP_DELAY_MS = 500;

  const renderTitle = () => {
    if (isClickable) {
      return (
        <div
          className={clsx(
            // min-w-0 lets long titles truncate instead of pushing the
            // chevron slot out of the clipped title container.
            "flex items-center gap-1 min-w-0",
            titleAlignment === "center" ? "justify-center flex-1" : "",
          )}
        >
          <div
            className="flex items-center gap-1.5 group/title min-w-0"
            data-tooltip-id={tooltipAnchorId}
            data-tooltip-content={tooltipAnchorContent}
            data-tooltip-delay-show={info ? INFO_TOOLTIP_DELAY_MS : undefined}
          >
            <Typography
              className={clsx(
                "flex",
                titleOverflowVisible ? "min-w-0" : "truncate",
                TITLE_VARIANT_STYLES[variant],
              )}
              variant="subtitle3"
            >
              {title}
            </Typography>
            {info && (
              <InformationCircleIcon
                className={clsx(
                  BUTTON_STYLE.s.icon,
                  "text-foreground-secondary group-hover/title:!text-slate-900 dark:group-hover/title:!text-foreground shrink-0 invisible group-hover/tile:visible",
                )}
              />
            )}
          </div>
          <div
            className={clsx(
              BUTTON_STYLE.xs.width,
              "flex items-center justify-center shrink-0",
            )}
          >
            <ChevronRightIcon
              className={clsx(
                BUTTON_STYLE.s.icon,
                "text-foreground-secondary group-hover/tile:!text-slate-900 dark:group-hover/tile:!text-foreground group-open:rotate-90 invisible group-hover/tile:visible",
              )}
            />
          </div>
        </div>
      );
    }

    return (
      <div
        className={clsx(
          "flex items-center gap-1.5 cursor-default group/title min-w-0",
          titleAlignment === "center" ? "justify-center flex-1" : "",
        )}
        data-tooltip-id={tooltipAnchorId}
        data-tooltip-content={tooltipAnchorContent}
        data-tooltip-delay-show={info ? INFO_TOOLTIP_DELAY_MS : undefined}
      >
        <Typography
          className={clsx(
            "flex",
            titleOverflowVisible ? "min-w-0" : "truncate",
            titleAlignment === "center" ? "justify-center flex-1" : "",
            TITLE_VARIANT_STYLES[variant],
          )}
          variant={titleVariant}
        >
          {title}
        </Typography>
        {info && (
          <InformationCircleIcon
            className={clsx(
              BUTTON_STYLE.s.icon,
              "text-slate-400 dark:text-foreground-muted group-hover/title:!text-slate-900 dark:group-hover/title:!text-foreground shrink-0 invisible group-hover/tile:visible",
            )}
          />
        )}
      </div>
    );
  };
  return (
    <div
      {...rest}
      className={twMerge(
        "hover:[&_button:not([role=tab]).tertiary]:bg-surface-tertiary dark:hover:[&_button:not([role=tab]).tertiary]:bg-surface-secondary relative group/tile",
        PROPERTY_TILE_VARIANT_STYLES[size].padding,
        symmetricPadding
          ? PROPERTY_TILE_VARIANT_STYLES[size].symmetricPadding
          : "",
        isClickable
          ? "cursor-pointer hover:bg-slate-150 dark:hover:bg-slate-800/20"
          : "",
        rest.className,
      )}
    >
      <div
        className={clsx(
          "text-slate-900 dark:text-foreground flex items-center gap-2 relative",
        )}
      >
        {leading}
        {(title || subtitle) && (
          <PropertyTileText
            className={clsx(
              "z-10 mr-auto",
              titleOverflowVisible ? "min-w-0" : "truncate",
              titleAlignment === "center" ? "flex-1" : "",
            )}
            title={title && renderTitle()}
            subtitle={subtitle}
            subtitleVariant={subtitleVariant}
            subtitleClassName={clsx(
              variant === "secondary" && "text-slate-500/80",
              titleAlignment === "center" && "justify-center flex-1",
            )}
          />
        )}

        <div
          className={twMerge(
            `flex gap-2 items-center justify-end`,
            "min-h-7",
            !overflowVisible && "overflow-hidden p-1 -m-1",
          )}
        >
          {onReset && (
            <Button
              data-tooltip-id="global"
              data-tooltip-content={
                resetTooltip ?? `Reset ${extractFirstText(title).toLowerCase()}`
              }
              size="s"
              className={twMerge(
                "ml-auto",
                resetButtonClassName,
                resetEnabled ? "!opacity-50 hover:!opacity-100" : "!opacity-0",
              )}
              onClick={(e) => {
                e.stopPropagation();
                onReset();
              }}
              variant="tertiary"
              disabled={!resetEnabled}
            >
              <ReactSVG
                src={"/svg/reset_arrow.svg"}
                className={`${BUTTON_STYLE.m.icon} fill-current`}
              />
            </Button>
          )}
          {(trailing || trailingPadding) && (
            <div
              className={twMerge(
                "flex flex-1 items-center justify-end gap-2",
                fitButtonLayout ? "!w-48" : "",
                !overflowVisible && "overflow-hidden p-1 -m-1",
                trailingWrapperClassName,
              )}
            >
              {trailing}
              {((!onReset && !trailing) || trailingPadding) && (
                <div
                  className={clsx(BUTTON_STYLE.s.width, BUTTON_STYLE.s.height)}
                />
              )}
            </div>
          )}
        </div>
        {/* {isClickable && (
          <div className="absolute -left-3.5 top-1/2 -translate-y-1/2 items-center justify-center hidden group-[.group]:flex">
            <ChevronRightIcon
              className={clsx(
                "text-slate-500 group-hover/summary:!text-slate-900 dark:group-hover/summary:!text-white group-open:rotate-90 size-2.5 invisible group-hover/pannel:visible"
              )}
            />
          </div>
        )} */}
      </div>
      {/* Per-instance tooltip used only for ReactNode `info`. String info
       *  goes through the global tooltip's data-tooltip-content path and
       *  doesn't need this. */}
      {info && !isStringInfo && (
        <CustomTooltip id={richInfoTooltipId} delayShow={INFO_TOOLTIP_DELAY_MS} className={infoTooltipClassName}>
          {info}
        </CustomTooltip>
      )}
    </div>
  );
};

export default PropertyTile;
